import { createFileRoute, Link } from '@tanstack/react-router'
import {
  Activity,
  ArrowRight,
  BarChart3,
  Gauge,
  Headset,
  MapPin,
  Network,
  RefreshCw,
  Repeat,
  Smartphone,
  Timer,
  TrendingUp,
  Users,
} from 'lucide-react'
import { TrialOffer } from '@/components/marketing/TrialOffer'
import { formatCapGb, PlanGrid } from '@/components/PricingSection'
import { SupportDialog } from '@/components/support/SupportDialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import type { PricingPlan } from '@/lib/billing/billing'
import { getPublicPlans } from '@/lib/billing/server'
import { stickyWindowLong } from '@/lib/credentials/credentials'
import { pageLinks, pageMeta, SITE_NAME, SITE_URL } from '@/lib/seo'

export const Route = createFileRoute('/pricing')({
  loader: () => getPublicPlans(),
  head: ({ loaderData }) => ({
    meta: pageMeta({
      title: 'US Mobile Proxy Pricing, Plans from $30/mo · Masklabs',
      description:
        'US mobile proxy plans from $30/mo, from Starter (5 GB) to Scale (400 GB). One shared data pool per team, refill anytime at your plan rate, no surprise overages.',
      path: '/pricing',
    }),
    links: pageLinks('/pricing'),
    // Service + Offer structured data — one Offer per plan, for search and
    // AI-answer engines. `Service`, not `Product`: Google runs Product markup
    // through its merchant-listing checks (image, shipping, returns, reviews)
    // that don't apply to a subscription and show up as Search Console
    // warnings. Prices are the plans straight from the loader, so this can
    // never drift from what the grid renders. The FAQPage entity is built from
    // the same `faqs` array the page renders, for the same reason.
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Service',
          name: 'Masklabs US Mobile Proxies',
          serviceType: 'Mobile proxy network',
          description:
            'US mobile proxy plans on real carrier IPs over HTTP, HTTPS, and SOCKS5, metered by the byte with a shared data pool per team.',
          provider: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
          areaServed: 'US',
          url: `${SITE_URL}/pricing`,
          offers: (loaderData?.plans ?? []).map((plan) => ({
            '@type': 'Offer',
            name: `${plan.name} (${plan.includedGb} GB / month)`,
            price: String(plan.priceMonthlyMicroUsd / 1_000_000),
            priceCurrency: 'USD',
            availability: 'https://schema.org/InStock',
            url: `${SITE_URL}/pricing`,
          })),
        }),
      },
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: faqsFor(loaderData?.plans ?? []).map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }),
      },
    ],
  }),
  component: Pricing,
})

/** What the product does — summarized from the feature story on the home page. */
const capabilities = [
  {
    icon: Repeat,
    title: 'Rotating & sticky sessions',
    body: `A fresh IP on every request, or pin one for ${stickyWindowLong()} with a username suffix.`,
  },
  {
    icon: Network,
    title: 'HTTP, HTTPS, SOCKS5 & HTTP/3',
    body: 'Every protocol on every credential, same login on all four. HTTPS runs on port 443 for TLS-only networks. HTTP/3 adds UDP for QUIC.',
  },
  {
    icon: Gauge,
    title: 'Metered to the byte',
    body: 'Usage debits your pool exactly. At zero, credentials pause instead of billing you.',
  },
  {
    icon: Users,
    title: 'A credential per service',
    body: 'Up to 50 per organization, each with its own usage line and limits.',
  },
  {
    icon: RefreshCw,
    title: 'Rotate on demand',
    body: 'Cycle a password or burn a sticky session instantly from the dashboard.',
  },
  {
    icon: Timer,
    title: 'Instant setup',
    body: 'Signup to first request in minutes, with no sales call needed and no minimum commit.',
  },
]

const trust = [
  {
    icon: BarChart3,
    title: 'Usage stats dashboard',
    body: 'Watch your data drain live, per credential, as traffic flows.',
  },
  {
    icon: MapPin,
    title: 'City-level targeting at no extra cost',
    body: 'Pin traffic to a specific US city with a suffix on your login.',
  },
  {
    icon: Smartphone,
    title: 'Real mobile device IPs',
    body: 'Exit addresses from real phones on carrier networks, not a datacenter.',
  },
  {
    icon: TrendingUp,
    title: 'Growing US pool',
    body: 'Nationwide carrier coverage that we keep adding capacity to.',
  },
  {
    icon: Activity,
    title: 'Fast and reliable',
    body: 'Connections establish fast, on a gateway built to stay up.',
  },
  {
    icon: Headset,
    title: '24/7 support',
    body: 'Get help whenever a job breaks, day or night.',
  },
]

/**
 * The top-up limit answer is assembled from the live catalog so the FAQ (and
 * its FAQPage schema, built from the same array) can never drift from what
 * checkout actually enforces (`topUpCapGb` is a monthly cap).
 */
function topUpLimitAnswer(plans: PricingPlan[]): string {
  const groups = new Map<number, string[]>()
  for (const p of plans) {
    groups.set(p.topUpCapGb, [...(groups.get(p.topUpCapGb) ?? []), p.name])
  }
  const parts = [...groups.entries()].map(
    ([cap, names]) => `${names.join(' and ')} up to ${formatCapGb(cap)}`,
  )
  return `Yes, per month and by plan: ${parts.join(', ')}. Within your monthly allowance, add data as often as you need at your plan's per-GB rate.`
}

const faqsFor = (plans: PricingPlan[]) => [
  {
    q: 'How does billing work?',
    a: 'Pick a monthly plan and your organization gets its full data pool up front. Usage drains the pool byte for byte, live in the dashboard, and the pool resets to the full amount at the start of each billing cycle.',
  },
  {
    q: 'What if I need more data mid-month?',
    a: "Add data at your plan's per-GB rate, the same rate as your plan data. Added data is prepaid: you pay before you use it, so there is nothing to bill you for after the fact. Usage spends your plan's pool first, then the data you added.",
  },
  {
    q: 'Is there a limit on how much data I can add?',
    a: topUpLimitAnswer(plans),
  },
  {
    q: 'What happens when my data runs out?',
    a: 'Your credentials pause instead of running up a bill. Add data and they pick right back up. A runaway job can cost you at most the balance you chose to buy, never a surprise invoice.',
  },
  {
    q: 'How is usage measured?',
    a: 'By the byte. We debit exactly the traffic that moves, with no rounding up to 100 MB or 1 GB blocks, and report it live per credential in the dashboard.',
  },
  {
    q: 'Does unused plan data roll over?',
    a: 'No. Plan data resets to the full amount each billing cycle, and additional data you buy applies to that same cycle, so buy what you expect to use rather than stockpiling it.',
  },
]

function Pricing() {
  const { plans, trial } = Route.useLoaderData()
  return (
    <main className="w-full text-lg">
      <section className="border-b">
        <div className="mx-auto w-full max-w-3xl px-6 py-20">
          <h1 className="text-4xl font-bold tracking-tight text-balance sm:text-5xl">
            Plans for every workload.
          </h1>
          <p className="mt-6 text-lg text-muted-foreground text-pretty">
            Every plan includes a monthly pool of data at that plan&apos;s
            per-GB rate, and it resets each cycle. Need more mid-month? Add data
            at the same rate, prepaid.
          </p>
        </div>
      </section>

      <section className="mx-auto w-full max-w-7xl px-6 py-16">
        <TrialOffer offer={trial} className="mb-6" />
        <PlanGrid plans={plans} />
        <p className="mt-6 text-sm text-muted-foreground">All prices in USD.</p>
      </section>

      {/* A full-width band rather than a fifth tile: the grid is xl:grid-cols-4,
          so a fifth card would break the row, and a sales tile inside a
          self-serve comparison muddies it. */}
      <section className="mx-auto w-full max-w-7xl px-6 pb-16">
        <div className="flex flex-col items-start justify-between gap-4 rounded-lg border bg-muted/30 p-6 sm:flex-row sm:items-center">
          <div className="flex flex-col gap-1">
            <h2 className="font-semibold">Need something different?</h2>
            <p className="text-sm text-muted-foreground">
              Larger volumes, a custom allowance, or paying by invoice instead
              of card. Talk to us and we will put together terms that fit.
            </p>
          </div>
          <SupportDialog
            topic="sales"
            title="Talk to sales"
            description="Tell us the volume and terms you need and we will come back with a quote."
          >
            <Button variant="outline">Contact sales</Button>
          </SupportDialog>
        </div>
      </section>

      <section className="border-t bg-muted/30">
        <div className="mx-auto w-full max-w-5xl px-6 py-20">
          <h2 className="text-2xl font-bold tracking-tight">
            Why teams pick Masklabs
          </h2>
          <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[...capabilities, ...trust].map((t) => (
              <Card key={t.title}>
                <CardContent className="flex h-full flex-col gap-4 p-6">
                  <div className="flex size-10 items-center justify-center rounded-lg border bg-primary/10 text-primary">
                    <t.icon className="size-5" />
                  </div>
                  <div className="flex flex-col gap-2">
                    <h3 className="text-base font-semibold">{t.title}</h3>
                    <p className="text-sm text-muted-foreground text-pretty">
                      {t.body}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t">
        <div className="mx-auto w-full max-w-3xl px-6 py-20">
          <h2 className="text-2xl font-bold tracking-tight">
            Frequently asked
          </h2>
          <dl className="mt-8 flex flex-col divide-y">
            {faqsFor(plans).map((f) => (
              <div key={f.q} className="flex flex-col gap-2 py-5">
                <dt className="font-medium">{f.q}</dt>
                <dd className="text-sm text-muted-foreground text-pretty">
                  {f.a}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="border-t bg-muted/30">
        <div className="mx-auto w-full max-w-5xl px-6 py-20 text-center">
          <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-balance sm:text-4xl">
            Start on Starter. Upgrade when you outgrow it.
          </h2>
          <Button asChild size="lg" className="mt-8">
            <Link to="/signup" search={{ plan: 'starter' }}>
              Get started
              <ArrowRight data-icon="inline-end" />
            </Link>
          </Button>
        </div>
      </section>
    </main>
  )
}
